import { useEffect, type SyntheticEvent } from "react"
import { useAtom, useSetAtom } from "jotai"
import InputAdornment from '@mui/material/InputAdornment'
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import Autocomplete from "@mui/material/Autocomplete"
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import TextField from "@mui/material/TextField"
import { useLocation } from 'react-router-dom'

import {
    selectedCollectionCompanyIdAtom,
    selectedCollectionPointIdAtom,
    selectedMunicipalityIdAtom,
} from "@/state"
import { useCollectionCompanies } from "@/hooks/api/use-collection-company"
import { ROUTE_SEGMENTS } from "@/constants/general"

export default function CollectionCompanySelector() {

    const [collectionCompanyId, setCollectionCompanyId] = useAtom(selectedCollectionCompanyIdAtom)
    const setCollectionPointId = useSetAtom(selectedCollectionPointIdAtom)
    const setMunicipalityId = useSetAtom(selectedMunicipalityIdAtom)

    const { data, error } = useCollectionCompanies()

    /*  biome-ignore lint: useEffect deps
        We want to run this in reaction to collectionCompanyId change
    */
    useEffect(() => {
        // If there is no collectionCompanyId selected in the /app section, pick first one available in the array
        if (!collectionCompanyId && data && data.length > 0) {
            setCollectionCompanyId(data[0].id)
        }
        // If collectionCompanyId is out of the given array, pick the first one available in the array
        if (collectionCompanyId && data && data.length > 0 && !data.find(collectionCompany => collectionCompany.id === collectionCompanyId)) {
            setCollectionCompanyId(data[0].id)
        }
    }, [data])

    const options = data ? data.sort((a, b) => a.eoko_customer?.name.localeCompare(b.eoko_customer?.name, 'cs', { numeric: true })) : []

    const location = useLocation()
    const locationSlugs = location.pathname.slice(1).split('/')
    const disabled = locationSlugs[2] === ROUTE_SEGMENTS.motivationProgram && locationSlugs.length > 3

    return (
        <Autocomplete
            value={
                collectionCompanyId
                    ? options.find((option) => {
                        return collectionCompanyId === option.id
                    }) ?? null
                    : null
            }
            getOptionLabel={(option) => {
                return option.eoko_customer?.name ?? 'Svozová společnost'
            }}
            getOptionKey={(option) => {
                return option.id
            }}
            title={disabled ? 'Při vytváření a editaci motivačního modelu nelze měnit obec.' : null}
            renderOption={(props, option, { inputValue }) => {
                const matches = match(option.eoko_customer?.name ?? 'Svozová společnost', inputValue)
                const parts = parse(option.eoko_customer?.name ?? 'Svozová společnost', matches)

                const text = (parts.map((part, index) => (
                    <span
                        key={index}
                        style={{
                            fontWeight: part.highlight ? 700 : 400,
                        }}
                    >
                        {part.text}
                    </span>
                )))

                return (
                    <li {...props} key={props.key}>
                        <div>
                            {text}
                        </div>
                    </li>
                );
            }}
            onChange={(_event: SyntheticEvent, newValue) => {
                setCollectionCompanyId(newValue ? newValue.id : null)
                if (newValue) {
                    setMunicipalityId(null)
                    setCollectionPointId(null)
                }
            }}
            disableClearable
            id="collectionCompany-selector"
            options={options}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Svozová společnost"
                    size="small"
                    inputProps={{
                        ...params.inputProps,
                        // disable autocomplete and autofill
                        autoComplete: "off",
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start" sx={{ paddingLeft: 1}}>
                                {error ?
                                    <ErrorOutlineIcon sx={{ color: 'error.light' }} />
                                    :
                                    <LocalShippingRoundedIcon sx={{ color: 'customGrey.selectorIcon' }} />
                                }
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: 5,
                            backgroundColor: 'white',
                        }
                    }}
                    autoComplete="off"
                    sx={{
                        minWidth: 160,
                        borderRadius: 50,
                    }}
                />
            )}
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: "white" 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.light'
                    },
                }
              }}
        />
    )
}
